import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';

const Footer = ({ showTrivia, setShowTrivia }) => {
  const currentYear = new Date().getFullYear();
  const { darkMode, toggleDarkMode } = useTheme();
  const [showDiscordTooltip, setShowDiscordTooltip] = useState(false);
  
  return (
    <footer className="bg-[#294C6F] text-white pt-12 pb-6 mt-12">
      <div className="max-w-6xl mx-auto px-6">
        {/* Main footer content */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8 text-center">
          {/* Quick Links column */}
          <div>
            <h3 className="text-xl font-bold mb-4">Quick Links</h3>
            <ul className="flex justify-center space-x-4 md:space-x-8">
              <li>
                <Link to="/" className="text-gray-300 hover:text-white transition-colors">Home</Link>
              </li>
              <li>
                <Link to="/portfolio" className="text-gray-300 hover:text-white transition-colors">Portfolio</Link>
              </li>
            </ul>
          </div>

          {/* Contact/Social Icons column */}
          <div>
            <h3 className="text-xl font-bold mb-4">Contact</h3>
            <div className="flex justify-center space-x-4">
              {/* Twitter Icon */}
              <a 
                href="https://twitter.com/Invictable" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-white transition-colors duration-300"
                aria-label="Twitter"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085a4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                </svg>
              </a>
              
              {/* Discord Icon */}
              <div 
                className="relative"
                onMouseEnter={() => setShowDiscordTooltip(true)}
                onMouseLeave={() => setShowDiscordTooltip(false)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-300 hover:text-white transition-colors duration-300" viewBox="0 0 127.14 96.36" fill="currentColor">
                  <path d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.89,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21h0A105.73,105.73,0,0,0,32.71,96.36,77.7,77.7,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,64.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1A105.25,105.25,0,0,0,126.6,80.22h0C129.24,52.84,122.09,29.11,107.7,8.07ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,46,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,46,96.12,53,91.08,65.69,84.69,65.69Z" />
                </svg>
                {showDiscordTooltip && (
                  <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white px-2 py-1 rounded text-xs whitespace-nowrap z-50">
                    Discord: invic
                  </div>
                )}
              </div>
              
              {/* Mail Icon */}
              <a 
                href="mailto:realinvictable@gmail.com" 
                className="text-gray-300 hover:text-white transition-colors duration-300"
                aria-label="Email"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </a>
            </div>
          </div>
          
          {/* Settings column */}
          <div>
            <h3 className="text-xl font-bold mb-4">Settings</h3>
            <div className="flex flex-col items-center space-y-4">
              <div className="flex items-center">
                <span className="mr-3 text-gray-300">Weapon Trivia</span>
                <div 
                  className={`relative w-12 h-6 transition-colors duration-300 rounded-full cursor-pointer ${showTrivia ? 'bg-[#2978C8]' : 'bg-gray-600'}`}
                  onClick={() => setShowTrivia(!showTrivia)}
                >
                  <div 
                    className={`absolute left-1 top-1 w-4 h-4 transition-transform duration-300 transform bg-white rounded-full ${showTrivia ? 'translate-x-6' : ''}`}
                  ></div>
                </div>
              </div>
              <div className="flex items-center">
                <span className="mr-3 text-gray-300">Dark Mode</span>
                <div 
                  className={`relative w-12 h-6 transition-colors duration-300 rounded-full cursor-pointer ${darkMode ? 'bg-[#2978C8]' : 'bg-gray-600'}`}
                  onClick={toggleDarkMode}
                >
                  <div 
                    className={`absolute left-1 top-1 w-4 h-4 transition-transform duration-300 transform bg-white rounded-full ${darkMode ? 'translate-x-6' : ''}`}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="pt-8 mt-8 border-t border-gray-700 text-center text-gray-400 text-sm">
          {/* <p>&copy; {currentYear} Invictable. All rights reserved.</p> */}
          {/* <p className="mt-2">
            <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
            {' • '}
            <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
          </p> */}
          <p className="mt-2">
            Acropolis Deathmatch Arena cinematics courtesy of <a 
              href="https://youtu.be/fkdapCs9dOU" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-300 hover:text-white transition-colors"
            >
              Bitzel
            </a>
          </p>
          <p className="mt-2">
           Hoplite weapons renders via <a 
              href="https://discord.com/channels/1134583981275349073/1313647145777107084/1313647145777107084" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-300 hover:text-white transition-colors"
            >
              Cosmic
            </a>
          </p>
          <p className="mt-2">
           Hoplite promotional renders via <a 
              href="https://www.hoplite.gg" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-300 hover:text-white transition-colors"
            >
              Hoplite.gg
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 